import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import analyticsApi from '~/apis/analytics';
import { StatBar } from '~/components/common';
import { ICallsResponse, ICallTypeStats } from '~/utils/interface/analytics';

const CallTypeCard = ({ title, stats }: { title: string; stats: ICallTypeStats }) => {
  const { t } = useTranslation();
  const total = stats.initiated || 1;

  return (
    <div className='flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-6'>
      <div className='flex items-baseline justify-between'>
        <h2 className='text-base font-semibold text-foreground'>{title}</h2>
        <span className='text-sm text-muted-foreground'>
          {t('pages.calls.averageDuration', { seconds: stats.avgDurationSeconds })}
        </span>
      </div>
      <StatBar label={t('pages.calls.answered')} value={stats.answered} total={total} />
      <StatBar label={t('pages.calls.missed')} value={stats.missed} total={total} tone='destructive' />
      <StatBar label={t('common.failed')} value={stats.failed} total={total} tone='destructive' />
      <StatBar label={t('pages.calls.declined')} value={stats.declined} total={total} tone='destructive' />
    </div>
  );
};

const Calls = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ICallsResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    analyticsApi
      .getCalls()
      .then((res) => setData(res.data.data))
      .catch(() => setError(t('pages.calls.loadError')))
      .finally(() => setIsLoading(false));
  }, [t]);

  return (
    <div>
      <h1 className='text-xl font-semibold text-foreground'>{t('pages.calls.title')}</h1>
      <p className='mt-1 text-sm text-muted-foreground'>{t('pages.calls.description')}</p>

      {isLoading && <p className='mt-6 text-sm text-muted-foreground'>{t('common.loading')}</p>}
      {error && <p className='mt-6 text-sm text-destructive'>{error}</p>}

      {data && (
        <div className='mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <CallTypeCard title={t('pages.calls.voice')} stats={data.voice} />
          <CallTypeCard title={t('pages.calls.video')} stats={data.video} />
        </div>
      )}
    </div>
  );
};

export default Calls;
